/* eslint-disable max-lines */
//
//  TokenUtils.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { ShadowType, SupernovaError, TextCase, TokenGroup, TokenTheme, Unit, UnreachableCaseError, VisibilityType, } from "../exports";
import { DIMENSION_TOKEN_TYPES, TokenType, } from "../model/enums/SDKTokenType";
import { ThemeUtilities } from "../model/themes/SDKThemeUtilities";
import { BlurToken } from "../model/tokens/SDKBlurToken";
import { BorderToken } from "../model/tokens/SDKBorderToken";
import { ColorToken } from "../model/tokens/SDKColorToken";
import { BorderWidthToken, DimensionCategoryToken, DimensionToken, DurationToken, FontSizeToken, LetterSpacingToken, LineHeightToken, OpacityToken, ParagraphSpacingToken, RadiusToken, SizeToken, SpaceToken, ZIndexToken, } from "../model/tokens/SDKDimensionToken";
import { GradientToken } from "../model/tokens/SDKGradientToken";
import { ShadowToken } from "../model/tokens/SDKShadowToken";
import { FontFamilyToken, FontWeightToken, ProductCopyToken, StringCategoryToken, StringToken, } from "../model/tokens/SDKStringToken";
import { TextCaseToken } from "../model/tokens/SDKTextCaseToken";
import { TextDecorationToken } from "../model/tokens/SDKTextDecorationToken";
import { replaceReferencedTokenIds, } from "../model/tokens/SDKTokenValue";
import { TypographyToken } from "../model/tokens/SDKTypographyToken";
import { VisibilityToken } from "../model/tokens/SDKVisibilityToken";
import { DTTokenReferenceResolver } from "../tools/design-tokens/utilities/SDKDTTokenReferenceResolver";
import { sureOf } from "./CommonUtils";
import { blurDefaultValue, borderDefaultValue, dimensionDefaultValues, shadowTokenDefaultValue, typographyTokenDefaultValue, } from "./tokenDefaultValues";
import cloneDeep from "lodash/cloneDeep";
import flatMap from "lodash/flatMap";
import { v4 as uuidv4 } from "uuid";
const formatTokenTypeName = (tokenType) => tokenType
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    // capitalize first letter
    .replace(/^./, (char) => char.toUpperCase());
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Implementation
export class TokenUtils {
    /** Map tokens for lookup */
    static tokensToReferenceMap(tokens) {
        const map = new Map();
        tokens.forEach((token) => {
            map.set(token.id, token);
        });
        return map;
    }
    static isReferenceResolutionFailed(token) {
        var _a;
        const anyToken = token;
        const hasReference = Array.isArray(anyToken.value)
            ? anyToken.value.some((v) => v.referencedTokenId)
            : !!anyToken.value.referencedTokenId;
        return !!((_a = token.origin) === null || _a === void 0 ? void 0 : _a.referenceOriginId) && !hasReference;
    }
    static isReferenceResolutionFailedById(tokenReferenceId, tokensMap) {
        if (!tokenReferenceId) {
            return false;
        }
        const token = tokensMap.get(tokenReferenceId);
        if (!token) {
            return true;
        }
        return TokenUtils.isReferenceResolutionFailed(token);
    }
    /**
     * Checks if resolution of token or any underlying token has failed
     */
    static hasAnyReferenceResolutionFailed(token, tokensMap) {
        if (this.isReferenceResolutionFailed(token)) {
            return true;
        }
        if (token.tokenType === TokenType.typography) {
            const typographyToken = token;
            const propertiesToCheck = [
                typographyToken.value.fontFamily,
                typographyToken.value.fontSize,
                typographyToken.value.fontWeight,
                typographyToken.value.textDecoration,
                typographyToken.value.textCase,
                typographyToken.value.lineHeight,
                typographyToken.value.letterSpacing,
                typographyToken.value.paragraphIndent,
                typographyToken.value.paragraphSpacing,
            ];
            return propertiesToCheck.some((property) => property &&
                this.isReferenceResolutionFailedById(property.referencedTokenId, tokensMap));
        }
        return false;
    }
    /** Map token groups for lookup */
    static tokenGroupsToReferenceMap(groups) {
        const map = new Map();
        groups.forEach((group) => {
            map.set(group.id, group);
        });
        return map;
    }
    static lookupReference(id, tokens) {
        if (!id) {
            return undefined;
        }
        const token = tokens.get(id);
        if (token !== undefined) {
            return token;
        }
        return undefined;
    }
    static isDimensionToken(token) {
        var _a;
        return DIMENSION_TOKEN_TYPES.includes((_a = token === null || token === void 0 ? void 0 : token.tokenType) !== null && _a !== void 0 ? _a : "");
    }
    static isDifferentTypeValue(token) {
        if (!this.isDimensionToken(token)) {
            return {
                isDifferentTypeValue: false,
            };
        }
        const unit = DimensionCategoryToken.getCompatibleUnit(token.value.unit, token.tokenType);
        if (unit === token.value.unit) {
            return {
                isDifferentTypeValue: false,
            };
        }
        let errorMessage = `${formatTokenTypeName(token.tokenType)} token can only reference values ${unit === Unit.raw
            ? `without units (used: ${token.value.unit.toLowerCase()}).`
            : `in ${unit.toLowerCase()}.`}`;
        switch (token.tokenType) {
            case TokenType.dimension:
            case TokenType.lineHeight:
                errorMessage = `${formatTokenTypeName(token.tokenType)} token can only reference values in pixels, percentages, rem, or without units (used: ${token.value.unit.toLowerCase()}).`;
                break;
            case TokenType.size:
            case TokenType.space:
            case TokenType.fontSize:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.radius: {
                errorMessage = `${formatTokenTypeName(token.tokenType)} token can only reference values in pixels, percentages, or rem (used: ${token.value.unit.toLowerCase()}).`;
                break;
            }
            case TokenType.opacity:
                if (token.value.referencedTokenId) {
                    errorMessage = `${formatTokenTypeName(token.tokenType)} token cannot reference other dimensions. Remove the alias in Figma to resolve issue.`;
                }
                break;
            default:
                break;
        }
        return {
            isDifferentTypeValue: true,
            errorMessage,
        };
    }
    static isTokenOutOfRange(token) {
        var _a;
        if (!this.isDimensionToken(token)) {
            return {
                isTokenOutOfRange: false,
            };
        }
        const isReference = ((_a = token.origin) === null || _a === void 0 ? void 0 : _a.id) !== undefined;
        switch (token.tokenType) {
            case TokenType.dimension:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
                return {
                    isTokenOutOfRange: false,
                };
            case TokenType.size:
            case TokenType.space:
            case TokenType.fontSize:
            case TokenType.lineHeight:
            case TokenType.borderWidth:
            case TokenType.radius:
            case TokenType.duration:
                if (token.value.measure < 0) {
                    return {
                        isTokenOutOfRange: true,
                        errorMessage: `${formatTokenTypeName(token.tokenType)} token can only ${isReference ? "reference" : "contain"} values equal to or greater than 0.`,
                    };
                }
                return {
                    isTokenOutOfRange: false,
                };
            case TokenType.opacity: {
                const measure = DimensionCategoryToken.getCompatibleMeasure(token.value.unit, token.tokenType, token.value.measure);
                if (token.value.unit === Unit.raw && (measure < 0 || measure > 1)) {
                    return {
                        isTokenOutOfRange: true,
                        errorMessage: `${formatTokenTypeName(token.tokenType)} token can only ${isReference ? "reference" : "contain"} values between 0 and 100.`,
                    };
                }
                return {
                    isTokenOutOfRange: false,
                };
            }
            case TokenType.zIndex: {
                return {
                    isTokenOutOfRange: !Number.isInteger(token.value.measure),
                    errorMessage: `${formatTokenTypeName(token.tokenType)} tokens can only ${isReference ? "reference" : "contain"} integer values.`,
                };
            }
            default:
                return {
                    isTokenOutOfRange: false,
                };
        }
    }
    /** Creates a new token group for a given token type. Default values will be used for all properties */
    static createDefaultTokenGroup(type, versionId, brandId, name) {
        return new TokenGroup({
            persistentId: uuidv4(),
            brandId,
            tokenType: type,
            designSystemVersionId: versionId,
            isRoot: false,
            meta: {
                name: name !== null && name !== void 0 ? name : "Group",
                description: "",
            },
            childrenIds: [],
            createdAt: new Date().toISOString(),
        });
    }
    /** Creates a new token theme. Default values will be used for all properties */
    static createDefaultTokenTheme(versionId, brandId) {
        return new TokenTheme({
            persistentId: uuidv4(),
            brandId,
            designSystemVersionId: versionId,
            meta: {
                name: "Dark",
                description: "",
            },
            codeName: "dark",
            overrides: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        }, versionId);
    }
    /** Creates a new token for a given token type. Default values will be used for all properties */
    static createDefaultToken(type, versionId, brandId, properties) {
        switch (type) {
            case TokenType.color:
                return this.createDefaultColorToken(versionId, brandId, properties);
            case TokenType.typography:
                return this.createDefaultTypographyToken(versionId, brandId, properties);
            case TokenType.dimension:
                return this.createDefaultDimensionToken(versionId, brandId, properties);
            case TokenType.size:
                return this.createDefaultSizeToken(versionId, brandId, properties);
            case TokenType.space:
                return this.createDefaultSpaceToken(versionId, brandId, properties);
            case TokenType.opacity:
                return this.createDefaultOpacityToken(versionId, brandId, properties);
            case TokenType.fontSize:
                return this.createDefaultFontSizeToken(versionId, brandId, properties);
            case TokenType.lineHeight:
                return this.createDefaultLineHeightToken(versionId, brandId, properties);
            case TokenType.letterSpacing:
                return this.createDefaultLetterSpacingToken(versionId, brandId, properties);
            case TokenType.paragraphSpacing:
                return this.createDefaultParagraphSpacingToken(versionId, brandId, properties);
            case TokenType.borderWidth:
                return this.createDefaultBorderWidthToken(versionId, brandId, properties);
            case TokenType.radius:
                return this.createDefaultRadiusToken(versionId, brandId, properties);
            case TokenType.duration:
                return this.createDefaultDurationToken(versionId, brandId, properties);
            case TokenType.zIndex:
                return this.createDefaultZIndexToken(versionId, brandId, properties);
            case TokenType.shadow:
                return this.createDefaultShadowToken(versionId, brandId, properties);
            case TokenType.border:
                return this.createDefaultBorderToken(versionId, brandId, properties);
            case TokenType.gradient:
                return this.createDefaultGradientToken(versionId, brandId, properties);
            case TokenType.string:
                return this.createDefaultStringToken(versionId, brandId, properties);
            case TokenType.productCopy:
                return this.createDefaultProductCopyToken(versionId, brandId, properties);
            case TokenType.fontFamily:
                return this.createDefaultFontFamilyToken(versionId, brandId, properties);
            case TokenType.fontWeight:
                return this.createDefaultFontWeightToken(versionId, brandId, properties);
            case TokenType.textCase:
                return this.createDefaultTextCaseToken(versionId, brandId, properties);
            case TokenType.textDecoration:
                return this.createDefaultTextDecorationToken(versionId, brandId, properties);
            case TokenType.visibility:
                return this.createDefaultVisibilityToken(versionId, brandId, properties);
            case TokenType.blur:
                return this.createDefaultBlurToken(versionId, brandId, properties);
            default:
                // NOTE: a typecheck if the switch is exhaustive
                // if you add a new token type a type error would pop up here
                throw new UnreachableCaseError(type);
        }
    }
    /** Creates a new default color token */
    static createDefaultColorToken(versionId, brandId, properties) {
        const value = "#FFFFFF";
        return ColorToken.create(versionId, brandId, "Token", "", value, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default typography token */
    static createDefaultTypographyToken(versionId, brandId, properties) {
        return TypographyToken.create(versionId, brandId, "Token", "", typographyTokenDefaultValue, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, new DTTokenReferenceResolver(), properties, []);
    }
    /** Creates a new default typography token */
    static createDefaultGradientToken(versionId, brandId, properties) {
        const value = "linear-gradient(0deg, #e66465 0%, #9198e5 100%)";
        return GradientToken.create(versionId, brandId, "Token", "", value, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, new DTTokenReferenceResolver(), properties, []);
    }
    /** Creates a new default dimension token */
    static createDefaultDimensionToken(versionId, brandId, properties) {
        // @ts-expect-error TS(2322): Type 'DimensionCategoryToken<AnyDimensionTokenValu... Remove this comment to see the full error message
        return DimensionToken.create(TokenType.dimension, versionId, brandId, "Token", "", dimensionDefaultValues[TokenType.dimension], 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default size token */
    static createDefaultSizeToken(versionId, brandId, properties) {
        // @ts-expect-error TS(2322): Type 'DimensionCategoryToken<AnyDimensionTokenValu... Remove this comment to see the full error message
        return SizeToken.create(TokenType.size, versionId, brandId, "Token", "", dimensionDefaultValues[TokenType.size], 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default space token */
    static createDefaultSpaceToken(versionId, brandId, properties) {
        // @ts-expect-error TS(2322): Type 'DimensionCategoryToken<AnyDimensionTokenValu... Remove this comment to see the full error message
        return SpaceToken.create(TokenType.space, versionId, brandId, "Token", "", dimensionDefaultValues[TokenType.space], 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default opacity token */
    static createDefaultOpacityToken(versionId, brandId, properties) {
        // @ts-expect-error TS(2322): Type 'DimensionCategoryToken<AnyDimensionTokenValu... Remove this comment to see the full error message
        return OpacityToken.create(TokenType.opacity, versionId, brandId, "Token", "", dimensionDefaultValues[TokenType.opacity], 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default line height token */
    static createDefaultLineHeightToken(versionId, brandId, properties) {
        // @ts-expect-error TS(2322): Type 'DimensionCategoryToken<AnyDimensionTokenValu... Remove this comment to see the full error message
        return LineHeightToken.create(TokenType.lineHeight, versionId, brandId, "Token", "", dimensionDefaultValues[TokenType.lineHeight], 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default letter spacing token */
    static createDefaultLetterSpacingToken(versionId, brandId, properties) {
        // @ts-expect-error TS(2322): Type 'DimensionCategoryToken<AnyDimensionTokenValu... Remove this comment to see the full error message
        return LetterSpacingToken.create(TokenType.letterSpacing, versionId, brandId, "Token", "", dimensionDefaultValues[TokenType.letterSpacing], 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default paragraph spacing token */
    static createDefaultParagraphSpacingToken(versionId, brandId, properties) {
        // @ts-expect-error TS(2322): Type 'DimensionCategoryToken<AnyDimensionTokenValu... Remove this comment to see the full error message
        return ParagraphSpacingToken.create(TokenType.paragraphSpacing, versionId, brandId, "Token", "", dimensionDefaultValues[TokenType.paragraphSpacing], 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default border token */
    static createDefaultBorderWidthToken(versionId, brandId, properties) {
        // @ts-expect-error TS(2322): Type 'DimensionCategoryToken<AnyDimensionTokenValu... Remove this comment to see the full error message
        return BorderWidthToken.create(TokenType.borderWidth, versionId, brandId, "Token", "", dimensionDefaultValues[TokenType.borderWidth], 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default border radius token */
    static createDefaultRadiusToken(versionId, brandId, properties) {
        // @ts-expect-error TS(2322): Type 'DimensionCategoryToken<AnyDimensionTokenValu... Remove this comment to see the full error message
        return RadiusToken.create(TokenType.radius, versionId, brandId, "Token", "", dimensionDefaultValues[TokenType.radius], 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default font size token */
    static createDefaultFontSizeToken(versionId, brandId, properties) {
        // @ts-expect-error TS(2322): Type 'DimensionCategoryToken<AnyDimensionTokenValu... Remove this comment to see the full error message
        return FontSizeToken.create(TokenType.fontSize, versionId, brandId, "Token", "", dimensionDefaultValues[TokenType.fontSize], 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default font family token */
    static createDefaultFontFamilyToken(versionId, brandId, properties) {
        const value = "Inter";
        // @ts-expect-error TS(2322): Type 'StringCategoryToken<AnyStringTokenValue>' is... Remove this comment to see the full error message
        return FontFamilyToken.create(TokenType.fontFamily, versionId, brandId, "Token", "", value, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default font weight token */
    static createDefaultFontWeightToken(versionId, brandId, properties) {
        const value = "Regular";
        // @ts-expect-error TS(2322): Type 'StringCategoryToken<AnyStringTokenValue>' is... Remove this comment to see the full error message
        return FontWeightToken.create(TokenType.fontWeight, versionId, brandId, "Token", "", value, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default duration token */
    static createDefaultDurationToken(versionId, brandId, properties) {
        // @ts-expect-error TS(2322): Type 'DimensionCategoryToken<AnyDimensionTokenValu... Remove this comment to see the full error message
        return DurationToken.create(TokenType.duration, versionId, brandId, "Token", "", dimensionDefaultValues[TokenType.duration], 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default z-index token */
    static createDefaultZIndexToken(versionId, brandId, properties) {
        // @ts-expect-error TS(2322): Type 'DimensionCategoryToken<AnyDimensionTokenValu... Remove this comment to see the full error message
        return ZIndexToken.create(TokenType.zIndex, versionId, brandId, "Token", "", dimensionDefaultValues[TokenType.zIndex], 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default text case token */
    static createDefaultTextCaseToken(versionId, brandId, properties) {
        const value = "Original";
        return TextCaseToken.create(versionId, brandId, "Token", "", value, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default text case token */
    static createDefaultTextDecorationToken(versionId, brandId, properties) {
        const value = "None";
        return TextDecorationToken.create(versionId, brandId, "Token", "", value, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default visibility token */
    static createDefaultVisibilityToken(versionId, brandId, properties) {
        const value = VisibilityType.visible;
        return VisibilityToken.create(versionId, brandId, "Token", "", value, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default generic token */
    static createDefaultBlurToken(versionId, brandId, properties) {
        return BlurToken.create(versionId, brandId, "Token", "", blurDefaultValue, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, new DTTokenReferenceResolver(), properties, []);
    }
    /** Creates a new default generic token */
    static createDefaultStringToken(versionId, brandId, properties) {
        const value = "";
        // @ts-expect-error TS(2322): Type 'StringCategoryToken<AnyStringTokenValue>' is... Remove this comment to see the full error message
        return StringToken.create(TokenType.string, versionId, brandId, "Token", "", value, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default generic token */
    static createDefaultProductCopyToken(versionId, brandId, properties) {
        const value = "";
        // @ts-expect-error TS(2322): Type 'StringCategoryToken<AnyStringTokenValue>' is... Remove this comment to see the full error message
        return ProductCopyToken.create(TokenType.productCopy, versionId, brandId, "Token", "", value, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, properties, []);
    }
    /** Creates a new default generic token */
    static createDefaultShadowToken(versionId, brandId, properties) {
        return ShadowToken.create(versionId, brandId, "Token", "", shadowTokenDefaultValue, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, new DTTokenReferenceResolver(), properties, []);
    }
    /** Creates a new default generic token */
    static createDefaultBorderToken(versionId, brandId, properties) {
        return BorderToken.create(versionId, brandId, "Token", "", borderDefaultValue, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined, new DTTokenReferenceResolver(), properties, []);
    }
    static replaceIdAcrossAllPossibleReferences(override, newId, allTokens) {
        const currentId = override.token.id;
        override.token.id = newId;
        this.replaceIdAcrossAllPossibleReferencesWithTokens(currentId, newId, allTokens.map((t) => t.token));
    }
    static replaceIdAcrossAllPossibleReferencesWithTokens(currentId, newId, allTokens) {
        const mappedTokens = TokenUtils.tokensToReferenceMap(allTokens);
        for (const t of allTokens) {
            const token = t;
            // Generic check for reference
            replaceReferencedTokenIds(token, currentId, newId, mappedTokens);
            TokenUtils.replaceIdForComplexTokens(token, currentId, newId);
        }
    }
    static replaceIdForComplexTokens(token, currentId, newId) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3;
        // Specific checks for tokens that can reference other values internally
        if (token instanceof TypographyToken) {
            if (((_a = token.value.fontWeight) === null || _a === void 0 ? void 0 : _a.referencedTokenId) &&
                ((_b = token.value.fontWeight) === null || _b === void 0 ? void 0 : _b.referencedTokenId) === currentId) {
                token.value.fontWeight.referencedTokenId = newId;
            }
            if (((_c = token.value.fontFamily) === null || _c === void 0 ? void 0 : _c.referencedTokenId) &&
                ((_d = token.value.fontFamily) === null || _d === void 0 ? void 0 : _d.referencedTokenId) === currentId) {
                token.value.fontFamily.referencedTokenId = newId;
            }
            if (((_e = token.value.fontSize) === null || _e === void 0 ? void 0 : _e.referencedTokenId) &&
                ((_f = token.value.fontSize) === null || _f === void 0 ? void 0 : _f.referencedTokenId) === currentId) {
                token.value.fontSize.referencedTokenId = newId;
            }
            if (((_g = token.value.letterSpacing) === null || _g === void 0 ? void 0 : _g.referencedTokenId) &&
                ((_h = token.value.letterSpacing) === null || _h === void 0 ? void 0 : _h.referencedTokenId) === currentId) {
                token.value.letterSpacing.referencedTokenId = newId;
            }
            if (((_j = token.value.lineHeight) === null || _j === void 0 ? void 0 : _j.referencedTokenId) &&
                ((_k = token.value.lineHeight) === null || _k === void 0 ? void 0 : _k.referencedTokenId) === currentId) {
                token.value.lineHeight.referencedTokenId = newId;
            }
            if (((_l = token.value.paragraphIndent) === null || _l === void 0 ? void 0 : _l.referencedTokenId) &&
                ((_m = token.value.paragraphIndent) === null || _m === void 0 ? void 0 : _m.referencedTokenId) === currentId) {
                token.value.paragraphIndent.referencedTokenId = newId;
            }
            if (((_o = token.value.paragraphSpacing) === null || _o === void 0 ? void 0 : _o.referencedTokenId) &&
                ((_p = token.value.paragraphSpacing) === null || _p === void 0 ? void 0 : _p.referencedTokenId) === currentId) {
                token.value.paragraphSpacing.referencedTokenId = newId;
            }
            if (((_q = token.value.textCase) === null || _q === void 0 ? void 0 : _q.referencedTokenId) &&
                ((_r = token.value.textCase) === null || _r === void 0 ? void 0 : _r.referencedTokenId) === currentId) {
                token.value.textCase.referencedTokenId = newId;
            }
            if (((_s = token.value.textDecoration) === null || _s === void 0 ? void 0 : _s.referencedTokenId) &&
                ((_t = token.value.textDecoration) === null || _t === void 0 ? void 0 : _t.referencedTokenId) === currentId) {
                token.value.textDecoration.referencedTokenId = newId;
            }
        }
        else if (token instanceof ShadowToken) {
            for (const tokenValue of token.value) {
                if (((_u = tokenValue.color) === null || _u === void 0 ? void 0 : _u.referencedTokenId) &&
                    ((_v = tokenValue.color) === null || _v === void 0 ? void 0 : _v.referencedTokenId) === currentId) {
                    tokenValue.color.referencedTokenId = newId;
                }
            }
        }
        else if (token instanceof BorderToken) {
            if (((_w = token.value.color) === null || _w === void 0 ? void 0 : _w.referencedTokenId) &&
                ((_x = token.value.color) === null || _x === void 0 ? void 0 : _x.referencedTokenId) === currentId) {
                token.value.color.referencedTokenId = newId;
            }
            if (((_y = token.value.width) === null || _y === void 0 ? void 0 : _y.referencedTokenId) &&
                ((_z = token.value.width) === null || _z === void 0 ? void 0 : _z.referencedTokenId) === currentId) {
                token.value.width.referencedTokenId = newId;
            }
        }
        else if (token instanceof GradientToken) {
            for (const tokenValue of token.value) {
                for (const stop of tokenValue.stops) {
                    if (((_0 = stop.color) === null || _0 === void 0 ? void 0 : _0.referencedTokenId) &&
                        ((_1 = stop.color) === null || _1 === void 0 ? void 0 : _1.referencedTokenId) === currentId) {
                        stop.color.referencedTokenId = newId;
                    }
                }
            }
        }
        else if (token instanceof BlurToken) {
            if (((_2 = token.value.radius) === null || _2 === void 0 ? void 0 : _2.referencedTokenId) &&
                ((_3 = token.value.radius) === null || _3 === void 0 ? void 0 : _3.referencedTokenId) === currentId) {
                token.value.radius.referencedTokenId = newId;
            }
        }
    }
    /** Creates a new theme with updated overrides */
    static tokenThemeByUpdatingOverride(theme, override) {
        const overrides = theme.overriddenTokens;
        const filteredOverrides = overrides.filter((o) => o.id !== override.id);
        filteredOverrides.push(override);
        theme.overriddenTokens = filteredOverrides;
        return theme;
    }
    /** Creates a new theme with removed override */
    static tokenThemeByRemovingOverride(theme, tokenId) {
        const overrides = theme.overriddenTokens;
        const filteredOverrides = overrides.filter((o) => o.id !== tokenId && o.idInVersion !== tokenId);
        theme.overriddenTokens = filteredOverrides;
        return theme;
    }
    /** Creates a new color token with updated .color value */
    static colorTokenByUpdatingColor(token, newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        if (newValue instanceof ColorToken) {
            update.value = {
                opacity: update.value.opacity,
                color: {
                    r: newValue.value.color.r,
                    g: newValue.value.color.g,
                    b: newValue.value.color.b,
                    referencedTokenId: newValue.value.color.referencedTokenId,
                },
                referencedTokenId: newValue.id,
            };
        }
        else {
            update.value = {
                opacity: update.value.opacity,
                color: {
                    r: newValue.r,
                    g: newValue.g,
                    b: newValue.b,
                    referencedTokenId: newValue.referencedTokenId,
                },
                referencedTokenId: null,
            };
        }
        return update;
    }
    /** Creates a new color token with updated .opacity value */
    static colorTokenByUpdatingOpacity(token, newValue, checkReference = true) {
        if (checkReference) {
            TokenUtils.checkNotReference(token);
        }
        const update = ThemeUtilities.replicateTokenWithValue(token);
        if (newValue instanceof OpacityToken) {
            update.value.opacity = {
                measure: newValue.value.measure,
                unit: newValue.value.unit,
                referencedTokenId: newValue.id,
            };
        }
        else {
            update.value.opacity = {
                measure: newValue,
                unit: Unit.raw,
                referencedTokenId: null,
            };
        }
        return update;
    }
    /** Creates a new blur token with updated .type value */
    static blurTokenByUpdatingType(token, newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value.type = newValue;
        return update;
    }
    /** Creates a new blur token with updated .radius value */
    static blurTokenByUpdatingRadius(token, 
    // @ts-expect-error TS(2344): Type 'DimensionTokenValue' does not satisfy the co... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value.radius = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new typography token with updated .fontFamily value */
    static typographyTokenByUpdatingFontFamily(token, 
    // @ts-expect-error TS(2344): Type 'FontFamilyTokenValue' does not satisfy the c... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        // it can reference any string token. Possible to achieve from figma plugin.
        if (newValue instanceof StringCategoryToken) {
            update.value.fontFamily = {
                text: newValue.value.text,
                referencedTokenId: newValue.id,
            };
        }
        else {
            update.value.fontFamily = {
                text: newValue.text,
                referencedTokenId: null,
            };
        }
        return update;
    }
    /** Creates a new typography token with updated .fontWeight value */
    static typographyTokenByUpdatingFontWeight(token, 
    // @ts-expect-error TS(2344): Type 'FontWeightTokenValue' does not satisfy the c... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        // it can reference any string token. Possible to achieve from figma plugin.
        if (newValue instanceof StringCategoryToken) {
            update.value.fontWeight = {
                text: newValue.value.text,
                referencedTokenId: newValue.id,
            };
        }
        else {
            update.value.fontWeight = {
                text: newValue.text,
                referencedTokenId: null,
            };
        }
        return update;
    }
    /** Creates a new typography token with updated .fontSize value */
    static typographyTokenByUpdatingFontSize(token, 
    // @ts-expect-error TS(2344): Type 'FontSizeTokenValue' does not satisfy the con... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value.fontSize = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new typography token with updated .letterSpacing value */
    static typographyTokenByUpdatingLetterSpacing(token, 
    // @ts-expect-error TS(2344): Type 'LetterSpacingTokenValue' does not satisfy th... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value.letterSpacing = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new typography token with updated .lineHeight value */
    static typographyTokenByUpdatingLineHeight(token, 
    // @ts-expect-error TS(2344): Type 'LineHeightTokenValue' does not satisfy the c... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value.lineHeight = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new typography token with updated .paragraphSpacing value */
    static typographyTokenByUpdatingParagraphSpacing(token, 
    // @ts-expect-error TS(2344): Type 'ParagraphSpacingTokenValue' does not satisfy... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value.paragraphSpacing = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new typography token with updated .paragraphSpacing value */
    static typographyTokenByUpdatingParagraphIndent(token, 
    // @ts-expect-error TS(2344): Type 'ParagraphSpacingTokenValue' does not satisfy... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value.paragraphIndent = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new typography token with updated .textCase value */
    static typographyTokenByUpdatingTextCase(token, 
    // @ts-expect-error TS(2344): Type 'TextCaseTokenValue' does not satisfy the con... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        if (newValue instanceof TextCaseToken) {
            update.value.textCase = {
                value: newValue.value.value,
                options: newValue.value.options,
                referencedTokenId: newValue.id,
            };
        }
        else {
            update.value.textCase = {
                value: newValue.value,
                options: newValue.options,
                referencedTokenId: null,
            };
        }
        return update;
    }
    /** Creates a new border token with updated .position value */
    static typographyTokenByUpdatingTextDecoration(token, 
    // @ts-expect-error TS(2344): Type 'TextDecorationTokenValue' does not satisfy t... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        if (newValue instanceof TextDecorationToken) {
            update.value.textDecoration = {
                value: newValue.value.value,
                options: newValue.value.options,
                referencedTokenId: newValue.id,
            };
        }
        else {
            update.value.textDecoration = {
                value: newValue.value,
                options: newValue.options,
                referencedTokenId: null,
            };
        }
        return update;
    }
    /** Creates a new border token with updated .color value. Do note, opacity is ignored */
    static borderTokenByUpdatingColor(token, newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        if (newValue instanceof ColorToken) {
            update.value.color = {
                opacity: update.value.color.opacity,
                color: {
                    r: newValue.value.color.r,
                    g: newValue.value.color.g,
                    b: newValue.value.color.b,
                    referencedTokenId: newValue.value.color.referencedTokenId,
                },
                referencedTokenId: newValue.id,
            };
        }
        else {
            update.value.color = {
                opacity: update.value.color.opacity,
                color: {
                    r: newValue.r,
                    g: newValue.g,
                    b: newValue.b,
                    referencedTokenId: newValue.referencedTokenId,
                },
                referencedTokenId: null,
            };
        }
        return update;
    }
    /** Creates a new border token with updated color.opacity value */
    static borderTokenByUpdatingOpacity(token, newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        if (newValue instanceof OpacityToken) {
            update.value.color.opacity = {
                measure: newValue.value.measure,
                unit: newValue.value.unit,
                referencedTokenId: newValue.id,
            };
        }
        else {
            update.value.color.opacity = {
                measure: newValue,
                unit: Unit.raw,
                referencedTokenId: null,
            };
        }
        return update;
    }
    /** Creates a new border token with updated .width value */
    static borderTokenByUpdatingWidth(token, 
    // @ts-expect-error TS(2344): Type 'BorderWidthTokenValue' does not satisfy the ... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value.width = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new border token with updated .position value */
    static borderTokenByUpdatingPosition(token, newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value.position = newValue;
        return update;
    }
    /** Creates a new border token with updated .style value */
    static borderTokenByUpdatingStyle(token, newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value.style = newValue;
        return update;
    }
    /** Creates a new dimension token with updated value */
    static dimensionTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'DimensionTokenValue' does not satisfy the co... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new size token with updated value */
    static sizeTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'SizeTokenValue' does not satisfy the constra... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new space token with updated value */
    static spaceTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'SpaceTokenValue' does not satisfy the constr... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new opacity token with updated value */
    static opacityTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'OpacityTokenValue' does not satisfy the cons... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new font size token with updated value */
    static fontSizeTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'FontSizeTokenValue' does not satisfy the con... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new font size token with updated value */
    static lineHeightTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'LineHeightTokenValue' does not satisfy the c... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new letter spacing token with updated value */
    static letterSpacingTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'LetterSpacingTokenValue' does not satisfy th... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new paragraph spacing token with updated value */
    static paragraphSpacingTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'ParagraphSpacingTokenValue' does not satisfy... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new border width token with updated value */
    static borderWidthTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'BorderWidthTokenValue' does not satisfy the ... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new radius token with updated value */
    static radiusTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'RadiusTokenValue' does not satisfy the const... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new duration token with updated value */
    static durationTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'DurationTokenValue' does not satisfy the con... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new z-index token with updated value */
    static zIndexTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'ZIndexTokenValue' does not satisfy the const... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedDimensionValue(newValue);
        return update;
    }
    /** Creates a new string token with updated value */
    static stringTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'StringTokenValue' does not satisfy the const... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedStringValue(newValue);
        return update;
    }
    /** Creates a new product copy token with updated value */
    static productCopyTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'ProductCopyTokenValue' does not satisfy the ... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedStringValue(newValue);
        return update;
    }
    /** Creates a new font family token with updated value */
    static fontFamilyTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'FontFamilyTokenValue' does not satisfy the c... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedStringValue(newValue);
        return update;
    }
    /** Creates a new font weight token with updated value */
    static fontWeightTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'FontWeightTokenValue' does not satisfy the c... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = this.rawOrReferencedStringValue(newValue);
        return update;
    }
    /** Creates a new text case token with updated value */
    static textCaseTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'TextCaseTokenValue' does not satisfy the con... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = {
            value: newValue.value,
            options: newValue.options,
            referencedTokenId: null,
        };
        return update;
    }
    /** Creates a new text case token with updated value */
    static textDecorationTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'TextDecorationTokenValue' does not satisfy t... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = {
            value: newValue.value,
            options: newValue.options,
            referencedTokenId: null,
        };
        return update;
    }
    /** Creates a new visibility token with updated value */
    static visibilityTokenByUpdatingValue(token, 
    // @ts-expect-error TS(2344): Type 'VisibilityTokenValue' does not satisfy the c... Remove this comment to see the full error message
    newValue) {
        this.checkNotReference(token);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = {
            value: newValue.value,
            options: newValue.options,
            referencedTokenId: null,
        };
        return update;
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Multi-layer token manipulation
    /** Creates a new shadow token with one new extra layer added at the end of all layers */
    static shadowTokenByAddingLayer(token) {
        const update = ThemeUtilities.replicateTokenWithValue(token);
        const layer = {
            color: {
                color: {
                    r: 0,
                    g: 0,
                    b: 0,
                    referencedTokenId: null,
                },
                opacity: {
                    measure: 1,
                    unit: Unit.raw,
                    referencedTokenId: null,
                },
                referencedTokenId: null,
            },
            opacity: {
                measure: 1,
                unit: Unit.raw,
                referencedTokenId: null,
            },
            radius: 0,
            spread: 0,
            type: ShadowType.drop,
            x: 0,
            y: 4,
            referencedTokenId: null,
        };
        update.value.push(layer);
        return update;
    }
    /** Creates a new shadow token by removing one of the layers */
    static shadowTokenByRemovingLayer(token, layerIndex) {
        this.validateLayerAccess(token, layerIndex);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        update.value = update.value.splice(layerIndex, 1);
        return update;
    }
    /** Creates a new shadow token with updated .color value. Do note, opacity is ignored */
    static shadowTokenByUpdatingColor(token, layerIndex, newValue) {
        this.validateLayerAccess(token, layerIndex);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        const layer = sureOf(update.value[layerIndex]);
        if (newValue instanceof ColorToken) {
            layer.color = {
                opacity: layer.color.opacity,
                color: {
                    r: newValue.value.color.r,
                    g: newValue.value.color.g,
                    b: newValue.value.color.b,
                    referencedTokenId: newValue.value.color.referencedTokenId,
                },
                referencedTokenId: newValue.id,
            };
        }
        else {
            layer.color = {
                opacity: layer.color.opacity,
                color: {
                    r: newValue.r,
                    g: newValue.g,
                    b: newValue.b,
                    referencedTokenId: newValue.referencedTokenId,
                },
                referencedTokenId: null,
            };
        }
        return update;
    }
    /** Creates a new shadow token with updated .color value */
    static shadowTokenByUpdatingOpacity(token, layerIndex, newValue) {
        this.validateLayerAccess(token, layerIndex);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        const layer = sureOf(update.value[layerIndex]);
        if (newValue instanceof OpacityToken) {
            layer.opacity = {
                measure: newValue.value.measure,
                unit: newValue.value.unit,
                referencedTokenId: newValue.id,
            };
        }
        else {
            layer.opacity = {
                measure: newValue,
                unit: Unit.raw,
                referencedTokenId: null,
            };
        }
        return update;
    }
    /** Creates a new shadow token with updated .x value */
    static shadowTokenByUpdatingX(token, layerIndex, newValue) {
        this.validateLayerAccess(token, layerIndex);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        const layer = sureOf(update.value[layerIndex]);
        layer.x = newValue;
        return update;
    }
    /** Creates a new shadow token with updated .y value */
    static shadowTokenByUpdatingY(token, layerIndex, newValue) {
        this.validateLayerAccess(token, layerIndex);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        const layer = sureOf(update.value[layerIndex]);
        layer.y = newValue;
        return update;
    }
    /** Creates a new shadow token with updated .radius value */
    static shadowTokenByUpdatingRadius(token, layerIndex, newValue) {
        this.validateLayerAccess(token, layerIndex);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        const layer = sureOf(update.value[layerIndex]);
        layer.radius = newValue;
        return update;
    }
    /** Creates a new shadow token with updated .spread value */
    static shadowTokenByUpdatingSpread(token, layerIndex, newValue) {
        this.validateLayerAccess(token, layerIndex);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        const layer = sureOf(update.value[layerIndex]);
        layer.spread = newValue;
        return update;
    }
    /** Creates a new shadow token with updated .type value */
    static shadowTokenByUpdatingType(token, layerIndex, newValue) {
        this.validateLayerAccess(token, layerIndex);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        const layer = sureOf(update.value[layerIndex]);
        layer.type = newValue;
        return update;
    }
    /** Creates a new gradient token with updated .to value */
    static gradientTokenByUpdatingTo(token, layerIndex, newValue) {
        this.validateLayerAccess(token, layerIndex);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        const layer = sureOf(update.value[layerIndex]);
        layer.to = newValue;
        return update;
    }
    /** Creates a new gradient token with updated .from value */
    static gradientTokenByUpdatingFrom(token, layerIndex, newValue) {
        this.validateLayerAccess(token, layerIndex);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        const layer = sureOf(update.value[layerIndex]);
        layer.from = newValue;
        return update;
    }
    /** Creates a new gradient token with updated .to value */
    static gradientTokenByUpdatingType(token, layerIndex, newValue) {
        this.validateLayerAccess(token, layerIndex);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        const layer = sureOf(update.value[layerIndex]);
        layer.type = newValue;
        return update;
    }
    /** Creates a new gradient token with updated .aspectRatio value */
    static gradientTokenByUpdatingAspectRatio(token, layerIndex, newValue) {
        this.validateLayerAccess(token, layerIndex);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        const layer = sureOf(update.value[layerIndex]);
        layer.aspectRatio = newValue;
        return update;
    }
    /** Creates a new gradient token with updated .to value */
    static gradientTokenByUpdatingStops(token, layerIndex, newValue) {
        TokenUtils.validateLayerAccess(token, layerIndex);
        const update = ThemeUtilities.replicateTokenWithValue(token);
        const layer = sureOf(update.value[layerIndex]);
        const stops = new Array();
        for (const v of newValue) {
            if (v.color instanceof ColorToken) {
                const stop = {
                    position: v.position,
                    color: {
                        opacity: v.color.value.opacity,
                        color: {
                            r: v.color.value.color.r,
                            g: v.color.value.color.g,
                            b: v.color.value.color.b,
                            referencedTokenId: v.color.value.color.referencedTokenId,
                        },
                        referencedTokenId: v.color.id,
                    },
                };
                stops.push(stop);
            }
            else {
                const stop = {
                    position: v.position,
                    color: {
                        opacity: v.color.opacity,
                        color: {
                            r: v.color.color.r,
                            g: v.color.color.g,
                            b: v.color.color.b,
                            referencedTokenId: v.color.color.referencedTokenId,
                        },
                        referencedTokenId: null,
                    },
                };
                stops.push(stop);
            }
        }
        layer.stops = stops;
        return update;
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Manipulating references
    /** Creates a new token where value is reference to the provided token. Token and reference must be of the same type */
    static tokenByReferencing(token, reference) {
        const update = ThemeUtilities.replicateTokenWithoutValue(token);
        if (token.id === reference.id) {
            throw SupernovaError.fromMessage("Can't create reference where token would reference itself");
        }
        update.value = Object.assign(Object.assign({}, cloneDeep(reference.value)), { referencedTokenId: reference.id });
        return update;
    }
    /** Creates a new multilayer token where one of the layers is reference to the provided token. Token and reference must be of the same type */
    static multilayerTokenByReferencing(token, layerIndex, reference) {
        const update = ThemeUtilities.replicateTokenWithValue(token);
        if (token.id === reference.id) {
            throw SupernovaError.fromMessage("Can't create reference where token would reference itself");
        }
        update.value[layerIndex] = Object.assign(Object.assign({}, cloneDeep(sureOf(reference.value[layerIndex]))), { referencedTokenId: reference.id });
        return update;
    }
    /** Creates a new token where value is raw, but taken from the previously referenced token. Token must have had reference before */
    static tokenByDisconnectingReference(token, options = {}) {
        const update = ThemeUtilities.replicateTokenWithoutValue(token);
        if (!token.value.referencedTokenId && !options.supressErrorOnNoReference) {
            throw SupernovaError.fromMessage("Can't create raw token from a token that isn't previously a reference");
        }
        update.value = Object.assign(Object.assign({}, cloneDeep(token.value)), { referencedTokenId: null });
        return update;
    }
    /** Creates a new multilayer token where one of the layers value is raw, but taken from the previously referenced token. That layer must have had reference before */
    static multilayerTokenByDisconnectingReference(token, 
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    layerIndex) {
        const update = ThemeUtilities.replicateTokenWithoutValue(token);
        if (!token.value.referencedTokenId) {
            throw SupernovaError.fromMessage("Can't create raw token from a token that isn't previously a reference");
        }
        // TODO: Multilayer shadow & gradient disconnect of the references
        return update;
    }
    /** Checks if token as a whole is a reference to another token */
    static isReferencedToken(token) {
        return (token.value.referencedTokenId !== null &&
            token.value.referencedTokenId !== undefined);
    }
    /** Checks if a token layer of multilayer token is a reference to another token */
    static isReferencedMultilayerTokenLayer(token, layerIndex) {
        this.validateLayerAccess(token, layerIndex);
        const layer = sureOf(token.value[layerIndex]);
        return (layer.referencedTokenId !== null && layer.referencedTokenId !== undefined);
    }
    /** Checks if a token was imported from some foreign source. Not imported tokens were always created in Supernova */
    static isTokenImported(token) {
        var _a, _b;
        return (((_a = token.origin) === null || _a === void 0 ? void 0 : _a.sourceId) !== null && ((_b = token.origin) === null || _b === void 0 ? void 0 : _b.sourceId) !== undefined);
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Conveniences
    static checkNotReference(token) {
        if (token.value.referencedTokenId) {
            throw SupernovaError.fromMessage("Performing value-replated operation on referenced token is not allowed");
        }
    }
    static rawOrReferencedDimensionValue(
    // @ts-expect-error TS(2344): Type 'T' does not satisfy the constraint '{ refere... Remove this comment to see the full error message
    newValue) {
        if (newValue instanceof DimensionCategoryToken) {
            return {
                measure: newValue.value.measure,
                unit: newValue.value.unit,
                referencedTokenId: newValue.id,
            };
        }
        return {
            measure: newValue.measure,
            unit: newValue.unit,
            referencedTokenId: null,
        };
    }
    static rawOrReferencedStringValue(
    // @ts-expect-error TS(2344): Type 'T' does not satisfy the constraint '{ refere... Remove this comment to see the full error message
    newValue) {
        if (newValue instanceof StringCategoryToken) {
            return {
                text: newValue.value.text,
                referencedTokenId: newValue.id,
            };
        }
        return {
            text: newValue.text,
            referencedTokenId: null,
        };
    }
    static validateLayerAccess(token, index) {
        let layers;
        if (token instanceof ShadowToken) {
            layers = token.shadowLayers;
        }
        else {
            layers = token.gradientLayers;
        }
        if (index < 0 || index >= layers.length) {
            throw SupernovaError.fromMessage(`Can't access layer at index ${index} for ${token.id}, layer outside of allowed bounds`);
        }
    }
    static inlineValueIntoComplexTokens(token, candidateId, candidateValueToInline) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3;
        switch (token.tokenType) {
            case TokenType.blur: {
                const t = token;
                if (((_b = (_a = t === null || t === void 0 ? void 0 : t.value) === null || _a === void 0 ? void 0 : _a.radius) === null || _b === void 0 ? void 0 : _b.referencedTokenId) === candidateId) {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    t.value.radius = candidateValueToInline;
                }
                break;
            }
            case TokenType.border: {
                const t = token;
                if (((_d = (_c = t === null || t === void 0 ? void 0 : t.value) === null || _c === void 0 ? void 0 : _c.color) === null || _d === void 0 ? void 0 : _d.referencedTokenId) === candidateId) {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    t.value.color = candidateValueToInline;
                }
                if (((_f = (_e = t === null || t === void 0 ? void 0 : t.value) === null || _e === void 0 ? void 0 : _e.width) === null || _f === void 0 ? void 0 : _f.referencedTokenId) === candidateId) {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    t.value.width = candidateValueToInline;
                }
                break;
            }
            case TokenType.gradient: {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-unsafe-optional-chaining
                for (const tv of token === null || token === void 0 ? void 0 : token.value) {
                    const stops = (_g = tv === null || tv === void 0 ? void 0 : tv.stops) !== null && _g !== void 0 ? _g : [];
                    for (const stop of stops) {
                        if (((_h = stop === null || stop === void 0 ? void 0 : stop.color) === null || _h === void 0 ? void 0 : _h.referencedTokenId) === candidateId) {
                            // TODO:fix-sdk-eslint
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            stop.color = candidateValueToInline;
                        }
                    }
                }
                break;
            }
            case TokenType.shadow: {
                const t = token;
                for (const tv of t.value) {
                    if (((_j = tv === null || tv === void 0 ? void 0 : tv.opacity) === null || _j === void 0 ? void 0 : _j.referencedTokenId) === candidateId) {
                        // TODO:fix-sdk-eslint
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        tv.opacity = candidateValueToInline;
                    }
                    if (((_k = tv === null || tv === void 0 ? void 0 : tv.color) === null || _k === void 0 ? void 0 : _k.referencedTokenId) === candidateId) {
                        // TODO:fix-sdk-eslint
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        tv.color = candidateValueToInline;
                    }
                }
                break;
            }
            case TokenType.typography: {
                const t = token;
                if (((_m = (_l = t === null || t === void 0 ? void 0 : t.value) === null || _l === void 0 ? void 0 : _l.fontSize) === null || _m === void 0 ? void 0 : _m.referencedTokenId) === candidateId) {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    t.value.fontSize = candidateValueToInline;
                }
                if (((_p = (_o = t === null || t === void 0 ? void 0 : t.value) === null || _o === void 0 ? void 0 : _o.letterSpacing) === null || _p === void 0 ? void 0 : _p.referencedTokenId) === candidateId) {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    t.value.letterSpacing = candidateValueToInline;
                }
                if (((_r = (_q = t === null || t === void 0 ? void 0 : t.value) === null || _q === void 0 ? void 0 : _q.lineHeight) === null || _r === void 0 ? void 0 : _r.referencedTokenId) === candidateId) {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    t.value.lineHeight = candidateValueToInline;
                }
                if (((_t = (_s = t === null || t === void 0 ? void 0 : t.value) === null || _s === void 0 ? void 0 : _s.paragraphIndent) === null || _t === void 0 ? void 0 : _t.referencedTokenId) === candidateId) {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    t.value.paragraphIndent = candidateValueToInline;
                }
                if (((_v = (_u = t === null || t === void 0 ? void 0 : t.value) === null || _u === void 0 ? void 0 : _u.paragraphSpacing) === null || _v === void 0 ? void 0 : _v.referencedTokenId) === candidateId) {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    t.value.paragraphSpacing = candidateValueToInline;
                }
                if (((_x = (_w = t === null || t === void 0 ? void 0 : t.value) === null || _w === void 0 ? void 0 : _w.fontFamily) === null || _x === void 0 ? void 0 : _x.referencedTokenId) === candidateId) {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    t.value.fontFamily = candidateValueToInline;
                }
                if (((_z = (_y = t === null || t === void 0 ? void 0 : t.value) === null || _y === void 0 ? void 0 : _y.fontWeight) === null || _z === void 0 ? void 0 : _z.referencedTokenId) === candidateId) {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    t.value.fontWeight = candidateValueToInline;
                }
                if (((_1 = (_0 = t === null || t === void 0 ? void 0 : t.value) === null || _0 === void 0 ? void 0 : _0.textCase) === null || _1 === void 0 ? void 0 : _1.referencedTokenId) === candidateId) {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    t.value.textCase = candidateValueToInline;
                }
                if (((_3 = (_2 = t === null || t === void 0 ? void 0 : t.value) === null || _2 === void 0 ? void 0 : _2.textDecoration) === null || _3 === void 0 ? void 0 : _3.referencedTokenId) === candidateId) {
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    t.value.textDecoration = candidateValueToInline;
                }
                break;
            }
        }
    }
    static getRefIdsFromComplexTokens(token) {
        var _a, _b, _c, _d;
        switch (token.tokenType) {
            case TokenType.blur:
                return [(_b = (_a = token.value) === null || _a === void 0 ? void 0 : _a.radius) === null || _b === void 0 ? void 0 : _b.referencedTokenId];
            case TokenType.border: {
                const value = token === null || token === void 0 ? void 0 : token.value;
                const refs = [value === null || value === void 0 ? void 0 : value.color, value === null || value === void 0 ? void 0 : value.width];
                return refs.map((r) => r === null || r === void 0 ? void 0 : r.referencedTokenId).filter(Boolean);
            }
            case TokenType.gradient: {
                const stops = flatMap((_c = token === null || token === void 0 ? void 0 : token.value) !== null && _c !== void 0 ? _c : [], (tv) => { var _a; return (_a = tv === null || tv === void 0 ? void 0 : tv.stops) !== null && _a !== void 0 ? _a : []; });
                return stops.map((r) => { var _a; return (_a = r === null || r === void 0 ? void 0 : r.color) === null || _a === void 0 ? void 0 : _a.referencedTokenId; }).filter(Boolean);
            }
            case TokenType.shadow: {
                const refs = flatMap((_d = token === null || token === void 0 ? void 0 : token.value) !== null && _d !== void 0 ? _d : [], (tv) => [
                    tv === null || tv === void 0 ? void 0 : tv.opacity,
                    tv === null || tv === void 0 ? void 0 : tv.color,
                ]);
                return refs.map((r) => r === null || r === void 0 ? void 0 : r.referencedTokenId).filter(Boolean);
            }
            case TokenType.typography: {
                const value = token === null || token === void 0 ? void 0 : token.value;
                const refs = [
                    value === null || value === void 0 ? void 0 : value.fontSize,
                    value === null || value === void 0 ? void 0 : value.letterSpacing,
                    value === null || value === void 0 ? void 0 : value.lineHeight,
                    value === null || value === void 0 ? void 0 : value.paragraphIndent,
                    value === null || value === void 0 ? void 0 : value.paragraphSpacing,
                    value === null || value === void 0 ? void 0 : value.fontFamily,
                    value === null || value === void 0 ? void 0 : value.fontWeight,
                    value === null || value === void 0 ? void 0 : value.textCase,
                    value === null || value === void 0 ? void 0 : value.textDecoration,
                ];
                return refs.map((r) => r === null || r === void 0 ? void 0 : r.referencedTokenId).filter(Boolean);
            }
            default:
                return [];
        }
    }
    static buildKey(path, name) {
        return [...path, name]
            .join("/")
            .toLowerCase()
            .replaceAll(" ", "-")
            .replaceAll("(", "")
            .replaceAll(")", "");
    }
    static buildReference(path, name) {
        return `{${[...path.slice(1), name].join(".")}}`;
    }
    static buildPath(token, groups) {
        if (!token.parentGroupId) {
            throw new Error("Keys can only be built for tokens that have parents");
        }
        let parent = groups.find((g) => g.id === token.parentGroupId);
        let segments = [];
        while (parent) {
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            segments = [parent.name, ...segments];
            const parentId = parent.parentGroupId;
            parent = parentId ? groups.find((g) => g.id === parentId) : undefined;
        }
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return segments;
    }
    static buildFallbackKey(node) {
        return this.buildKey(node.path, node.token.name)
            .replace(/^measure\//, `${this.mapTypeToPrefix(node.token.tokenType)}/`)
            .replace(/^generic\//, `${this.mapTypeToPrefix(node.token.tokenType)}/`)
            .replace(/^radius\//, `${this.mapTypeToPrefix(node.token.tokenType)}/`);
    }
    static buildFallbackKeyBack(node) {
        return this.buildKey(node.path, node.token.name)
            .replace(/^(dimension|font-size|font-sizing|line-height|letter-spacing|paragraph-spacing|spacing|space|opacity|size|sizing|border-width)\//, "measure/")
            .replace(/^(font-family|font-weight)\//, "generic/")
            .replace(/^(border-radius)\//, "radius/");
    }
    static buildFallbackKey2(node) {
        return this.buildKey(node.path, node.token.name)
            .replace(/^measure\//, `${this.mapTypeToPrefix2(node.token.tokenType)}/`)
            .replace(/^generic\//, `${this.mapTypeToPrefix(node.token.tokenType)}/`)
            .replace(/^radius\//, `${this.mapTypeToPrefix(node.token.tokenType)}/`);
    }
    static getTokenByKeyOrFallbackKeys(node, map) {
        var _a;
        const keys = TokenUtils.getKeysByKeyOrFallbackKeys(node);
        return map.get((_a = keys.find((key) => !!map.get(key))) !== null && _a !== void 0 ? _a : "");
    }
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    static getKeysByKeyOrFallbackKeys(node) {
        return [
            ...new Set([
                TokenUtils.buildKey(node.path, node.token.name),
                TokenUtils.buildFallbackKey(node),
                TokenUtils.buildFallbackKey2(node),
                // FIXMENOW: Verify and test
                TokenUtils.buildFallbackKeyBack(node),
            ]),
        ];
    }
    static getTokenByKeyOrBackwardKey(node, map) {
        var _a;
        const key = TokenUtils.buildKey(node.path, node.token.name);
        const fallbackKey = TokenUtils.buildFallbackKeyBack(node);
        return (_a = map.get(key)) !== null && _a !== void 0 ? _a : map.get(fallbackKey);
    }
    static getTextCaseTokenValue(value) {
        switch (value) {
            case TextCase.upper:
                return "Uppercase";
            case TextCase.lower:
                return "Lowercase";
            case TextCase.camel:
                return "Capitalize";
            case TextCase.smallCaps:
                return "Small caps";
            case TextCase.original:
                return "Original";
            default:
                throw new Error("Unknown text case");
        }
    }
}
TokenUtils.mapTypeToPrefix = (type) => {
    switch (type) {
        // Font Size as 2nd fallback
        case TokenType.size:
            return "sizing";
        case TokenType.fontSize:
            return "font-sizing";
        case TokenType.lineHeight:
            return "line-height";
        case TokenType.letterSpacing:
            return "letter-spacing";
        case TokenType.paragraphSpacing:
            return "paragraph-spacing";
        case TokenType.borderWidth:
            return "border-width";
        // Space as 2nd fallback
        case TokenType.space:
            return "spacing";
        case TokenType.radius:
            return "border-radius";
        case TokenType.fontFamily:
            return "font-family";
        case TokenType.fontWeight:
            return "font-weight";
        case TokenType.textCase:
            return "text-case";
        case TokenType.textDecoration:
            return "text-decoration";
        case TokenType.visibility:
            return "visibility";
        default:
            return type.toLowerCase().replaceAll(" ", "-");
    }
};
TokenUtils.mapTypeToPrefixBack = (type) => {
    switch (type) {
        // Font Size as 2nd fallback
        case TokenType.size:
            return "sizing";
        case TokenType.fontSize:
            return "font-sizing";
        case TokenType.lineHeight:
            return "line-height";
        case TokenType.letterSpacing:
            return "letter-spacing";
        case TokenType.paragraphSpacing:
            return "paragraph-spacing";
        case TokenType.borderWidth:
            return "border-width";
        // Space as 2nd fallback
        case TokenType.space:
            return "spacing";
        case TokenType.radius:
            return "radius";
        case TokenType.fontFamily:
            return "font-family";
        case TokenType.fontWeight:
            return "font-weight";
        default:
            return type.toLowerCase().replaceAll(" ", "-");
    }
};
TokenUtils.mapTypeToPrefix2 = (type) => {
    switch (type) {
        // Font Size as 2nd fallback
        case TokenType.fontSize:
            return "font-size";
        case TokenType.space:
            return "space";
        default:
            return type.toLowerCase().replaceAll(" ", "-");
    }
};
